// Enhanced Loading Indicator with Circular Spinner and Rotating Tips
import React, { useState, useEffect } from 'react';

// Array of helpful tips for users
const LOADING_TIPS = [
  "To see a detailed company profile tell me, 'Generate a detailed company profile for Microsoft.'",
  "Ask me to generate market calculations like TAM, SAM, and SOM.",
  "Share company lists to create customized industry ratios.",
  "Request competitive analysis by saying 'Compare Tesla vs Ford in the EV market.'",
  "Get market sizing data with 'What's the market size for cloud computing?'",
  "Ask for industry trends like 'What are the latest trends in fintech?'",
  "Generate SWOT analyses by saying 'Create a SWOT analysis for Amazon.'",
  "Request regulatory insights with 'What regulations affect the healthcare industry?'",
  "Get supply chain analysis by asking 'Analyze the supply chain for semiconductors.'",
  "Ask for investment thesis like 'Create an investment thesis for renewable energy.'",
  "Request customer segmentation with 'Who are the main customers for SaaS products?'",
  "Get pricing strategies by saying 'What pricing models work best for subscription services?'",
  "Ask for market entry strategies like 'How should a startup enter the AI market?'",
  "Request risk assessments with 'What are the key risks in cryptocurrency investing?'",
  "Get financial projections by saying 'Project revenue growth for the streaming industry.'",
  "Ask for merger analysis like 'Analyze the potential Disney-Netflix merger impact.'",
  "Request ESG analysis with 'What are Tesla's ESG performance metrics?'",
  "Get patent landscapes by asking 'What's the patent landscape for autonomous vehicles?'",
  "Ask for geographic analysis like 'Which regions show highest growth for e-commerce?'",
  "Request technology roadmaps with 'What's the roadmap for 5G technology adoption?'"
];

// --- FIXED SPINNER COMPONENT ---
const CircularSpinner = ({ size = 40, color = "#2563EB" }) => ( // Use a CSS color (e.g., hex) for the default
  <div className="flex justify-center">
    <div
      className="animate-spin rounded-full border-4 border-gray-200" // Dynamic class removed
      style={{
        width: `${size}px`,
        height: `${size}px`,
        borderTopColor: color // Color applied directly via style
      }}
    ></div>
  </div>
);

// Enhanced Loading Indicator Component
export const EnhancedLoadingIndicator = ({
  message = "DataWeb AI is thinking...",
  showTips = true,
  tipInterval = 6000 // 6 seconds
}) => {
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [currentTip, setCurrentTip] = useState(LOADING_TIPS[0]);

  // Rotate tips every 6 seconds
  useEffect(() => {
    if (!showTips) return;

    const interval = setInterval(() => {
      setCurrentTipIndex(prevIndex => {
        const nextIndex = (prevIndex + 1) % LOADING_TIPS.length;
        setCurrentTip(LOADING_TIPS[nextIndex]);
        return nextIndex;
      });
    }, tipInterval);

    return () => clearInterval(interval);
  }, [showTips, tipInterval]);

  return (
    <div className="flex justify-start mb-6">
      <div className="max-w-2xl w-full">
        <div className="flex items-start space-x-3">
          {/* AI Avatar */}
          <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>

          {/* Loading Content */}
          <div className="flex-1 bg-white dark:bg-gray-700 rounded-2xl rounded-bl-md px-6 py-4 shadow-sm border border-gray-200 dark:border-gray-600">
            {/* Main thinking message */}
            <div className="text-sm text-gray-700 dark:text-gray-300 mb-4 font-medium">
              {message}
            </div>

            {/* Circular Spinner - Centered on its own line */}
            <div className="mb-4">
              <CircularSpinner size={32} color="#2563EB" />
            </div>

            {/* Rotating Tips - On its own line */}
            {showTips && (
              <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                <div className="flex items-start space-x-2">
                  <div className="flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="text-xs font-semibold text-blue-600 dark:text-blue-400 mb-1">
                      Pro Tip:
                    </div>
                    <div
                      className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed transition-all duration-500 ease-in-out"
                      key={currentTipIndex} // Forces re-render for animation
                    >
                      {currentTip}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Updated Simple Loading Indicator (for backward compatibility)
export const SimpleLoadingIndicator = ({ message = "DataWeb AI is thinking..." }) => {
  return <EnhancedLoadingIndicator message={message} showTips={true} />;
};

// Alternative version without tips (if needed)
export const MinimalLoadingIndicator = ({ message = "DataWeb AI is thinking..." }) => {
  return <EnhancedLoadingIndicator message={message} showTips={false} />;
};

// Export default
export default EnhancedLoadingIndicator;